// The station sign-in handoff's secret, as the player web holds it.
//
// The PC opens (or shows a code for) `/station-sign-in#<token>`. The secret arrives in the URL's fragment, which a
// browser never sends to a server; the page reads it once, takes it out of the visible URL, and keeps it in
// `sessionStorage` only, with its expiry, so that it survives the sign-in round trip (a sign-in, or a provider's
// redirect, comes back to `/station-sign-in` without the fragment). It is never put in a query string, a log, a query
// key or analytics, and it is forgotten as soon as the handoff is finished, refused, cancelled or past its time.

/** The secret's shape: 32 random bytes, base64url (the contract's `SignInHandoffToken`). */
const HANDOFF_TOKEN = /^[A-Za-z0-9_-]{43}$/;

/** How long a handoff lives (the contract's three minutes), for the expiry kept until the server answers. */
export const HANDOFF_LIFETIME_MS = 180_000;

export const HANDOFF_STORAGE_KEY = 'clavian.stationSignIn';

export const isHandoffToken = (value: string): boolean => HANDOFF_TOKEN.test(value);

/**
 * The secret in a URL fragment: `#<token>`, or `#h=<token>`. `undefined` when there is no fragment at all, `null` when
 * there is one but it does not hold a secret.
 */
export function tokenFromFragment(hash: string): string | null | undefined {
  const fragment = hash.replace(/^#/, '');
  if (fragment === '') return undefined;
  const candidate = fragment.includes('=') ? (new URLSearchParams(fragment).get('h') ?? '') : fragment;
  return isHandoffToken(candidate) ? candidate : null;
}

interface StoredHandoff {
  readonly token: string;
  /** Epoch milliseconds. */
  readonly expiresAt: number;
  /**
   * The page was opened by the PC's own sign-in window (`?via=`), so it asks the server to sign this browser out once
   * the PC is signed in. Kept with the secret because a sign-in round trip comes back without the query.
   */
  readonly fromWindow?: boolean;
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The secret kept from an earlier visit to this page in this tab, while it is still alive. */
export function readStoredToken(now: number = Date.now()): string | null {
  return readStoredHandoff(now)?.token ?? null;
}

function readStoredHandoff(now: number): { readonly token: string; readonly fromWindow: boolean } | null {
  const store = storage();
  if (store === null) return null;
  try {
    const raw = store.getItem(HANDOFF_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredHandoff>;
    if (typeof parsed.token === 'string' && isHandoffToken(parsed.token) && typeof parsed.expiresAt === 'number' && parsed.expiresAt > now) {
      return { token: parsed.token, fromWindow: parsed.fromWindow === true };
    }
    store.removeItem(HANDOFF_STORAGE_KEY);
  } catch {
    // Unreadable: treat as absent.
  }
  return null;
}

export function rememberToken(token: string, expiresAt: number, fromWindow = false): void {
  try {
    const stored: StoredHandoff = fromWindow ? { token, expiresAt, fromWindow } : { token, expiresAt };
    storage()?.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage refused (a private window, a full quota): the page still works until it is left.
  }
}

export function forgetToken(): void {
  try {
    storage()?.removeItem(HANDOFF_STORAGE_KEY);
  } catch {
    // Nothing to forget.
  }
}

export type HandoffTokenState =
  | { readonly kind: 'ok'; readonly token: string; readonly fromFragment: boolean; readonly fromWindow: boolean }
  | { readonly kind: 'missing' }
  | { readonly kind: 'invalid' };

/** Where this visit's secret comes from: the fragment first, else what this tab kept, else nothing. */
export function resolveHandoffToken(hash: string, now: number = Date.now()): HandoffTokenState {
  const fragment = tokenFromFragment(hash);
  if (fragment === null) return { kind: 'invalid' };
  if (fragment !== undefined) return { kind: 'ok', token: fragment, fromFragment: true, fromWindow: false };
  const stored = readStoredHandoff(now);
  return stored === null ? { kind: 'missing' } : { kind: 'ok', token: stored.token, fromFragment: false, fromWindow: stored.fromWindow };
}
